import React, { useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  useTheme,
  Stack,
  Avatar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BusinessIcon from "@mui/icons-material/Business";
import { useFormik } from "formik";
import { SMAName } from "../../component/Schemas";

const fontFamily = `'Inter', 'SF Pro Display', 'Roboto', 'Arial', sans-serif`;

const EditSMADialog = ({ open, onClose, onSave, sma }) => {
  const theme = useTheme();

  const formik = useFormik({
    initialValues: {
      name: "",
      abbreviation: "",
      account: "",
    },
    validationSchema: SMAName,
    onSubmit: (values) => {
      const smaData = {
        abbreviation: values.abbreviation,
        account: values.account,
        managerId: sma?.managerId,
        name: values.name,
        smaId: sma?.fundId, // fundId is actually smaId for SMAs
        type: sma?.type || "Schwab"
      };
      onSave(smaData);
    },
  });

  // Update form values when sma prop changes
  useEffect(() => {
    if (sma && open) {
      // Extract account number from accounts object (first key)
      const accountNumber = sma?.accounts ? Object.keys(sma?.accounts)[0] : "";
      
      formik.setValues({
        name: sma.name || "",
        abbreviation: sma.abbreviation || "",
        account: accountNumber || "",
      });
    }
  }, [sma, open]);

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 4px 24px 0 rgba(16,30,54,0.10)",
          fontFamily,
          p: 0,
        },
      }}
    >
      <Box sx={{ px: 4, pt: 3, pb: 0 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar
              sx={{
                bgcolor: theme.palette.primary.main,
                width: 32,
                height: 32,
              }}
            >
              <BusinessIcon sx={{ color: "#fff", fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: 22,
                  fontFamily,
                  lineHeight: 1.2,
                }}
              >
                Edit SMA
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontFamily,
                  mt: 0.5,
                }}
              >
                Update SMA abbreviation and account details.
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={handleClose} size="small" sx={{ ml: 1 }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>
      <DialogContent sx={{ pt: 2, pb: 2, px: 4 }}>
        <form onSubmit={formik.handleSubmit}>
          <Stack spacing={2.5}>
            <TextField
              label="Name *"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              disabled
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              InputLabelProps={{
                shrink: true,
                sx: { fontWeight: 600, fontFamily },
              }}
              inputProps={{ style: { fontFamily } }}
              variant="outlined"
            />
            <TextField
              label="Abbreviation *"
              name="abbreviation"
              value={formik.values.abbreviation}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              error={formik.touched.abbreviation && Boolean(formik.errors.abbreviation)}
              helperText={formik.touched.abbreviation && formik.errors.abbreviation}
              InputLabelProps={{
                shrink: true,
                sx: { fontWeight: 600, fontFamily },
              }}
              inputProps={{ style: { fontFamily } }}
              variant="outlined"
            />
            <TextField
              label="Account *"
              name="account"
              value={formik.values.account}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              error={formik.touched.account && Boolean(formik.errors.account)}
              helperText={formik.touched.account && formik.errors.account}
              InputLabelProps={{
                shrink: true,
                sx: { fontWeight: 600, fontFamily },
              }}
              inputProps={{ style: { fontFamily } }}
              variant="outlined"
            />
          </Stack>
          
          <DialogActions sx={{ px: 0, pb: 0, pt: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{
                borderRadius: 1,
                fontWeight: 600,
                fontFamily,
                textTransform: "none",
                px: 3,
                minWidth: 120,
              }}
            >
              Update SMA
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditSMADialog;

